/**
 * @file (temporary) redirects for SEO valuable links in PromoPromo and myShopi.
 */
import type { ShopSlug } from "~/utils/redirectList";
import { redirectMyShopiPaths, shops } from "~/utils/redirectList";

const localeSegmentMap: Record<string, "fr" | "nl" | "nl-NL"> = {
  "fr-be": "fr",
  "nl-be": "nl",
};

const isOffersPath = (path: string, locale: string) => {
  if (locale === "fr") {
    return path.includes(`folder-offres`);
  } else return path.includes(`folder-aanbiedingen`);
};

const resolveDestination = (slug: ShopSlug, locale: "fr" | "nl" | "nl-NL") => {
  if (locale === "fr") return `/fr/${slug}/folder-offres`;
  if (locale === "nl") return `/nl/${slug}/folder-aanbiedingen`;
  return `/${slug}-aanbiedingen`;
};

/**
 * A temporary redirect handler for old myshopi links that have SEO value.
 * It's very hacky, but it's because it needs to somewhat gracefully handle ~20000 links
 *
 * The logic is as follows:
 *
 *  1. Fix foldre-offres paths, if its there, redirect.
 *  2. Try to filter out a store name from `/{locale}/{storename}/{entitytype}/{city?}`
 *  3. Redirect to a known set of stores that is already in production myShopi.
 *  4. If the store is not known redirect to the index page of shops.
 *
 * A special clause needs to be so it also finds
 */
export default defineNuxtRouteMiddleware((to) => {
  // the French shop-offers segment was historically misspelled as `foldre-offres`.
  // redirect to the correct spelling for SEO purposes.
  if (to.path.includes("foldre-offres")) {
    return navigateTo(
      {
        path: to.path.replace("foldre-offres", "folder-offres"),
        query: to.query,
      },
      { redirectCode: 301 },
    );
  }

  if (useCurrentHost().value === "allefolders") return;

  if (useCurrentHost().value === "myshopi") {
    const hasCashback = to.path.toLowerCase().includes("cashback");
    if (hasCashback) {
      const segments = to.path.split("/").filter(Boolean);
      const localeSegment = segments[0]?.toLowerCase();
      const locale = localeSegment === "fr" ? "fr" : "nl";
      const destination = `https://www.myshopi.com/${locale}/cashbacks`;
      if (to.path === `/${locale}/cashbacks`) {
        return;
      }

      return navigateTo(destination, {
        redirectCode: 301,
        external: true,
      });
    }

    // Folder overview pages: redirect to the new brochures index for SEO.
    if (to.path.startsWith("/nl-be/folders")) {
      return navigateTo("https://www.myshopi.com/nl/nieuw", {
        redirectCode: 301,
        external: true,
      });
    }
    if (to.path.startsWith("/fr/folders")) {
      return navigateTo("https://www.myshopi.com/fr/nouveaux", {
        redirectCode: 301,
        external: true,
      });
    }

    // Because legacy folder urls have more data than our ids, it's hard to map them internally.
    // old app just uses old site for now
    const isLegacyFolder = to.path.match(/^\/(nl-be|fr-be).*\/folders\/.*/);

    // legacy app uses a separate backend, so we redirect their newsletter unsubscribes
    const isNewsletterUnsubscribe = to.path.match(
      /^\/(nl-be|fr-be).*\/newsletter\/unsubscribe.*/,
    );

    if (isLegacyFolder || isNewsletterUnsubscribe) {
      return navigateTo(`https://old.myshopi.com${to.fullPath}`, {
        redirectCode: 301,
        external: true,
      });
    }

    // We use a limited list of known high-ranking urls with hard-coded, non-standard redirects
    const alternatePath = redirectMyShopiPaths[to.path];
    if (alternatePath) {
      return navigateTo(
        { path: alternatePath, query: to.query },
        { redirectCode: 301 },
      );
    }
  }

  const segments = to.path.split("/").filter(Boolean);
  if (!segments.length) return;
  const maybeLocale = segments[0]?.toLowerCase();
  if (!["nl-be", "fr-be"].includes(maybeLocale)) return;

  const locale = localeSegmentMap[maybeLocale ?? ""] ?? "nl";
  const startIndex = localeSegmentMap[maybeLocale ?? ""] ? 1 : null;

  if (!startIndex) return;

  const foundShopName = segments[startIndex];
  const slugSegment = shops.find(
    (shopName) =>
      shopName.slug.startsWith(foundShopName) ||
      foundShopName.startsWith(shopName.slug),
  );

  if (!slugSegment) {
    return navigateTo(
      { path: "/winkels", query: to.query },
      { redirectCode: 301, replace: false },
    );
  }

  const slug = slugSegment.slug.toLowerCase() as ShopSlug;

  if (isOffersPath(to.path, locale)) return;

  const destination = resolveDestination(slug, locale);
  if (to.path === destination) return;

  return navigateTo(
    { path: destination, query: to.query },
    { redirectCode: 301, replace: true },
  );
});
